import { Injectable } from '@angular/core';
import { PrimeNGConfig } from 'primeng/api';
import { LOCAL_STORAGE_KEY, StorageService } from './storage.service';
import { languages, LanguageData } from 'src/assets/lang';
import { TransformLabels } from '@app/shared/model/common.model';
import { AppConstants } from '@app/app.constant';

@Injectable({
    providedIn: 'root',
})
export class TranslationService {
    public language = this.storageService.getItem(
        LOCAL_STORAGE_KEY.SELECTED_LANGUAGE
    )
        ? this.storageService.getItem(LOCAL_STORAGE_KEY.SELECTED_LANGUAGE)
        : '1';

    constructor(
        private readonly primengConfig: PrimeNGConfig,
        private readonly storageService: StorageService
    ) {}

    /**
     * Returns the labels for the specified page key in the current language.
     * @param pageKey - The key of the page to get labels for
     */
    getLabels<PageKey extends keyof LanguageData>(
        pageKey: PageKey
    ): TransformLabels<LanguageData[PageKey]> {
        const languageData = languages[this.language as keyof typeof languages];
        const pageLabels = languageData[pageKey];
        return this.getLanguageLabels(pageLabels) as TransformLabels<
            LanguageData[PageKey]
        >;
    }

    /**
     * Returns the labels for the specified object in the current language.
     * @param obj - The object to get labels for
     */
    getLanguageLabels<T>(obj: T): TransformLabels<T> {
        const result = {} as TransformLabels<T>;

        for (const key in obj) {
            const value = obj[key];

            if (typeof value === 'string') {
                if (/{{\w+}}/.test(value)) {
                    result[key] = ((vars: Record<string, string>) =>
                        value.replace(
                            /{{(\w+)}}/g,
                            (_: string, varName: string) => vars[varName] ?? ''
                        )) as TransformLabels<T>[typeof key];
                } else {
                    result[key] = value as TransformLabels<T>[typeof key];
                }
            } else if (typeof value === 'object' && value !== null) {
                result[key] = this.getLanguageLabels(
                    value
                ) as TransformLabels<T>[typeof key];
            }
        }

        return result;
    }

    /**
     * Sets the selected language into PrimeNG configuration accordingly.
     */
    setLanguage(language?: string) {
        this.language = language!;
        if (this.language === '1') {
            this.primengConfig.setTranslation({
                firstDayOfWeek: 0,
                dayNamesMin: [`Su`, `Mo`, `Tu`, `We`, `Th`, `Fr`, `Sa`],
                monthNamesShort: [
                    `Jan`,
                    `Feb`,
                    `Mar`,
                    `Apr`,
                    `May`,
                    `Jun`,
                    `Jul`,
                    `Aug`,
                    `Sep`,
                    `Oct`,
                    `Nov`,
                    `Dec`,
                ],
                dateFormat:
                    AppConstants.multiLingualDateFormat.withoutTimeTwoYear['1'],
            });
        } else if (this.language === '2') {
            this.primengConfig.setTranslation({
                firstDayOfWeek: 1,
                dayNamesMin: [`Sö`, `Må`, `Ti`, `On`, `To`, `Fr`, `Lö`],
                monthNamesShort: [
                    `Jan`,
                    `Feb`,
                    `Mar`,
                    `Apr`,
                    `Maj`,
                    `Jun`,
                    `Jul`,
                    `Aug`,
                    `Sep`,
                    `Okt`,
                    `Nov`,
                    `Dec`,
                ],
                dateFormat:
                    AppConstants.multiLingualDateFormat.withoutTimeTwoYear['2'],
            });
        }
    }
}
